import { BaseQueryFn, createApi, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import {
  cleanIdFromTranscoderProfile,
  TranscoderAudioProfile,
  TranscoderAudioProfileResponse,
  TranscoderAudioProfilesQueryResult,
} from '../../models/transcoder';
import { fetchQueryWithToken } from '../auth.helpers';
import { getSettings, SettingsState } from '../settings.helpers';

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const settings = getSettings(api.getState() as SettingsState);
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = `${settings?.servers.serverTranscoderConfigurator ?? ''}/api/v1/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  return fetchQueryWithToken(adjustedArgs, api, extraOptions);
};

export const transcoderAudioProfileApi = createApi({
  reducerPath: 'transcoderAudioProfileApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['AudioProfile'],
  endpoints: builder => ({
    getTranscoderAudioProfiles: builder.query<TranscoderAudioProfile[], void>({
      query: () => 'audio-profile',
      providesTags: ['AudioProfile'],
    }),
    createTranscoderAudioProfile: builder.mutation<TranscoderAudioProfilesQueryResult, TranscoderAudioProfile>({
      query: audioProfile => {
        return {
          url: 'audio-profile',
          method: 'POST',
          body: cleanIdFromTranscoderProfile(audioProfile),
        };
      },
      invalidatesTags: ['AudioProfile'],
    }),
    updateTranscoderAudioProfile: builder.mutation<
      TranscoderAudioProfilesQueryResult,
      Partial<TranscoderAudioProfile> & Pick<TranscoderAudioProfile, 'id'>
    >({
      query: audioProfile => {
        return {
          url: `audio-profile`,
          method: 'PATCH',
          body: audioProfile,
        };
      },
      invalidatesTags: ['AudioProfile'],
    }),
    deleteTranscoderAudioProfile: builder.mutation<TranscoderAudioProfile, string>({
      query: id => ({
        url: `audio-profile/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AudioProfile'],
    }),
    getTranscoderAudioProfileByName: builder.query<TranscoderAudioProfileResponse, string>({
      query: name => `audio-profile/search/${name}`,
    }),
  }),
});

export const {
  useGetTranscoderAudioProfilesQuery,
  useGetTranscoderAudioProfileByNameQuery,
  useCreateTranscoderAudioProfileMutation,
  useUpdateTranscoderAudioProfileMutation,
  useDeleteTranscoderAudioProfileMutation,
} = transcoderAudioProfileApi;
